import { useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import PaystackPop from "@paystack/inline-js";
import { useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "core/store/authStore";
import { deliveryService } from "features/delivery/services/deliveryService";
import { initializeOrderPayment, verifyOrderPayment } from "features/delivery/services/orderApi";
import { queryKeys } from "core/query/queryKeys";
import { useOrderUpdates } from "shared/hooks/realtime/useOrderUpdates";
import type { OrderStatus } from "types/delivery.types";
import OrderTrackingMap from "features/tracking/components/OrderTrackingMap";
import AssignedDriverCard from "features/tracking/components/AssignedDriverCard";

const STATUS_STEPS: { key: OrderStatus; label: string; icon: string }[] = [
  { key: "pending", label: "Order Placed", icon: "receipt_long" },
  { key: "accepted", label: "Driver assigned", icon: "person_pin" },
  { key: "picked_up", label: "Picked Up", icon: "inventory_2" },
  { key: "in_transit", label: "In Transit", icon: "local_shipping" },
  { key: "delivered", label: "Delivered", icon: "check_circle" },
];

const ACTIVE_INDEX: Record<OrderStatus, number> = {
  awaiting_payment: 1,
  pending: 0,
  accepted: 1,
  picked_up: 2,
  in_transit: 3,
  delivered: 4,
  cancelled: -1,
  no_provider: -1,
};

function isFindingDriver(order: {
  status: string;
  driverId?: string | null;
  vehicleId?: string | null;
}): boolean {
  const noDriver = order.driverId == null || order.driverId === "";
  const noVehicle = order.vehicleId == null || order.vehicleId === "";
  return order.status === "pending" && noDriver && noVehicle;
}

export default function OrderTracking() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [payingNow, setPayingNow] = useState(false);
  const [paymentDone, setPaymentDone] = useState(false);
  const queryClient = useQueryClient();
  const { user, logout } = useAuthStore();
  const firstName = user?.displayName?.split(" ")[0] ?? "there";

  const orderQuery = useOrderUpdates(id);
  const order = orderQuery.data ?? null;
  const loading = orderQuery.isLoading;
  const [error, setError] = useState<string | null>(
    orderQuery.isError ? "Could not load order details." : null,
  );
  const [cancelling, setCancelling] = useState(false);

  const refetchOrder = () => void orderQuery.refetch();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const handleCancel = async () => {
    if (!id) return;
    setCancelling(true);
    try {
      await deliveryService.cancelOrder(id);
      await queryClient.invalidateQueries({ queryKey: queryKeys.orders.detail(id) });
      refetchOrder();
    } catch {
      setError("Could not cancel order. Please try again.");
    } finally {
      setCancelling(false);
    }
  };

  const handlePayNow = async () => {
    if (!id) return;
    setPayingNow(true);
    try {
      const { accessCode, reference } = await initializeOrderPayment(id);
      const popup = new PaystackPop();
      popup.newTransaction({
        key: import.meta.env.VITE_PAYSTACK_PUBLIC_KEY ?? "",
        email: "",
        amount: 0,
        accessCode,
        ref: reference,
        onSuccess: () => {
          setPayingNow(false);
          // Verify with backend so order transitions awaiting_payment → accepted
          verifyOrderPayment(id).then(() => {
            setPaymentDone(true);
            void queryClient.invalidateQueries({ queryKey: queryKeys.orders.detail(id) });
            refetchOrder();
          }).catch(() => {
            // Webhook may still fire; show confirming UI anyway
            setPaymentDone(true);
            void queryClient.invalidateQueries({ queryKey: queryKeys.orders.detail(id) });
            refetchOrder();
          });
        },
        onCancel: () => {
          setPayingNow(false);
        },
      });
    } catch {
      setError("Could not start payment. Please try again.");
      setPayingNow(false);
    }
  };

  const activeStep = order ? ACTIVE_INDEX[order.status as OrderStatus] : -1;
  const isCancelled = order?.status === "cancelled";
  const isAwaitingPayment = order?.status === "awaiting_payment";
  const isSearching = order ? isFindingDriver(order) : false;
  const isNoProvider = order?.status === "no_provider";
  const isDelivered = order?.status === "delivered";
  const showLiveBadge =
    order && !isCancelled && !isNoProvider && !isDelivered;

  return (
    <div className="min-h-screen bg-background-light font-display text-slate-900 overflow-x-hidden">
      {/* ── Top Nav ── */}
      <header className="sticky top-0 z-50 bg-background-light/85 backdrop-blur-xl border-b border-primary/8 px-6 lg:px-20 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/customer/dashboard" className="flex items-center gap-2.5">
            <div className="bg-primary p-1.5 rounded-xl shadow-lg shadow-primary/25">
              <span
                className="material-symbols-outlined text-white text-[22px]"
                style={{ fontVariationSettings: '"FILL" 1' }}
              >
                directions_run
              </span>
            </div>
            <h2 className="text-[1.4rem] font-black tracking-tighter">
              Errandi
            </h2>
          </Link>

          <div className="flex items-center gap-4">
            <div className="hidden sm:flex items-center gap-2.5 bg-white border border-slate-100 rounded-xl px-3.5 py-2 shadow-sm">
              <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center">
                <span className="text-primary font-black text-sm">
                  {firstName[0]?.toUpperCase()}
                </span>
              </div>
              <span className="text-sm font-semibold text-slate-700">
                {firstName}
              </span>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800 transition-colors px-3 py-2"
            >
              <span className="material-symbols-outlined text-[18px]">
                logout
              </span>
              <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 lg:px-8 py-10">
        {/* Back */}
        <Link
          to="/customer/dashboard"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-primary transition-colors mb-8"
        >
          <span className="material-symbols-outlined text-[18px]">
            arrow_back
          </span>
          Back to dashboard
        </Link>

        {loading && (
          <div className="flex items-center justify-center py-24">
            <span className="material-symbols-outlined text-4xl text-primary animate-spin">
              progress_activity
            </span>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
            <span className="material-symbols-outlined text-4xl text-red-400 mb-3 block">
              error_outline
            </span>
            <p className="text-slate-700 font-medium">{error}</p>
            <button
              onClick={refetchOrder}
              className="mt-4 text-sm font-semibold text-primary hover:underline"
            >
              Try again
            </button>
          </div>
        )}

        {order && !loading && (
          <>
            {/* Header card */}
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 mb-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-1">
                    Order Ref
                  </p>
                  <p className="text-sm font-bold text-slate-700">
                    #{order.id.split('-')[0].toUpperCase()}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {isCancelled ? (
                    <span className="inline-flex items-center gap-1.5 bg-red-50 text-red-500 text-xs font-bold px-3 py-1.5 rounded-full">
                      <span className="material-symbols-outlined text-[14px]">
                        cancel
                      </span>
                      Cancelled
                    </span>
                  ) : isAwaitingPayment ? (
                    <span className="inline-flex items-center gap-1.5 bg-amber-50 text-amber-600 text-xs font-bold px-3 py-1.5 rounded-full">
                      <span className="material-symbols-outlined text-[14px]" style={{ fontVariationSettings: '"FILL" 1' }}>
                        credit_card
                      </span>
                      Payment pending
                    </span>
                  ) : isNoProvider ? (
                    <span className="inline-flex items-center gap-1.5 bg-slate-100 text-slate-600 text-xs font-bold px-3 py-1.5 rounded-full">
                      No driver
                    </span>
                  ) : isDelivered ? (
                    <span className="inline-flex items-center gap-1.5 bg-green-50 text-green-600 text-xs font-bold px-3 py-1.5 rounded-full">
                      <span
                        className="material-symbols-outlined text-[14px]"
                        style={{ fontVariationSettings: '"FILL" 1' }}
                      >
                        check_circle
                      </span>
                      Delivered
                    </span>
                  ) : showLiveBadge ? (
                    <span className="inline-flex items-center gap-1.5 bg-primary/10 text-primary text-xs font-bold px-3 py-1.5 rounded-full">
                      <span className="material-symbols-outlined text-[14px] animate-pulse">
                        radio_button_checked
                      </span>
                      {isSearching ? "Finding driver" : "Live tracking"}
                    </span>
                  ) : null}
                </div>
              </div>
            </div>

            {order && !isCancelled && !isNoProvider && !isSearching && (
              <OrderTrackingMap order={order} />
            )}

            {order &&
              !isSearching &&
              !isNoProvider &&
              !isCancelled &&
              (order.driver ||
                order.driverId ||
                order.vehicle?.plateNumber) && (
                <AssignedDriverCard order={order} />
              )}

            {isAwaitingPayment && paymentDone && (
              <div className="bg-green-50 border border-green-200 rounded-2xl p-8 mb-6 text-center">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                  <span
                    className="material-symbols-outlined text-4xl text-green-500"
                    style={{ fontVariationSettings: '"FILL" 1' }}
                  >
                    check_circle
                  </span>
                </div>
                <h2 className="text-lg font-bold text-slate-900 mb-2">Payment confirmed!</h2>
                <p className="text-sm text-slate-500 max-w-sm mx-auto">
                  Your payment was received. Your driver will head to the pickup location shortly.
                </p>
                <div className="mt-4 flex items-center justify-center gap-2 text-xs font-semibold text-green-600">
                  <span className="material-symbols-outlined text-[16px] animate-spin">
                    progress_activity
                  </span>
                  Confirming with driver
                </div>
              </div>
            )}

            {isAwaitingPayment && !paymentDone && (
              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-8 mb-6">
                <div className="flex flex-col items-center text-center">
                  <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mb-4">
                    <span
                      className="material-symbols-outlined text-4xl text-amber-600"
                      style={{ fontVariationSettings: '"FILL" 1' }}
                    >
                      credit_card
                    </span>
                  </div>
                  <h2 className="text-lg font-bold text-slate-900 mb-2">Driver found! Complete payment</h2>
                  <p className="text-sm text-slate-500 mb-6 max-w-sm">
                    A driver has been matched to your order. Pay now to confirm your delivery.
                  </p>
                  <button
                    type="button"
                    onClick={handlePayNow}
                    disabled={payingNow}
                    className="w-full max-w-xs bg-primary text-white font-bold py-3.5 rounded-2xl shadow-lg shadow-primary/25 hover:bg-primary/90 disabled:opacity-60 transition-all flex items-center justify-center gap-2"
                  >
                    {payingNow ? (
                      <>
                        <span className="material-symbols-outlined text-[18px] animate-spin">
                          progress_activity
                        </span>
                        Opening payment…
                      </>
                    ) : (
                      <>
                        <span className="material-symbols-outlined text-[18px]" style={{ fontVariationSettings: '"FILL" 1' }}>
                          payments
                        </span>
                        Pay ₦{order?.amount?.toLocaleString()}
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancel}
                    disabled={cancelling}
                    className="mt-4 text-sm font-semibold text-red-500 hover:text-red-600 disabled:opacity-50"
                  >
                    {cancelling ? "Cancelling…" : "Cancel order"}
                  </button>
                </div>
              </div>
            )}

            {isSearching && (
              <div className="bg-primary/5 border border-primary/20 rounded-2xl p-8 mb-6 text-center">
                <span className="material-symbols-outlined text-5xl text-primary animate-spin mb-4 block">
                  progress_activity
                </span>
                <h2 className="text-lg font-bold text-slate-900 mb-2">Finding a driver…</h2>
                <p className="text-sm text-slate-500 mb-6 max-w-sm mx-auto">
                  We&apos;re matching your order with nearby providers. This usually takes a moment.
                </p>
                <button
                  type="button"
                  onClick={handleCancel}
                  disabled={cancelling}
                  className="text-sm font-semibold text-red-600 hover:text-red-700 disabled:opacity-50"
                >
                  {cancelling ? "Cancelling…" : "Cancel order"}
                </button>
              </div>
            )}

            {isNoProvider && (
              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
                <div className="flex items-start gap-4">
                  <span
                    className="material-symbols-outlined text-3xl text-amber-600"
                    style={{ fontVariationSettings: '"FILL" 1' }}
                  >
                    directions_car_off
                  </span>
                  <div className="flex-1">
                    <h2 className="font-bold text-slate-900 mb-1">No drivers available</h2>
                    <p className="text-sm text-slate-600 mb-4">
                      We couldn&apos;t find a provider for this delivery right now.
                    </p>
                    <div className="flex flex-wrap gap-3">
                      <button
                        type="button"
                        onClick={handleCancel}
                        disabled={cancelling}
                        className="text-sm font-bold text-slate-700 border border-slate-300 px-4 py-2 rounded-xl disabled:opacity-50"
                      >
                        {cancelling ? "Cancelling…" : "Cancel order"}
                      </button>
                      <Link
                        to="/deliverypage"
                        className="text-sm font-bold text-primary px-4 py-2 rounded-xl bg-white border border-primary/30"
                      >
                        Place new order
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Status timeline — only when order is progressing (not cancelled / finding driver / no provider) */}
            {!isCancelled && !isSearching && !isNoProvider && (
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 mb-6">
                <h2 className="text-base font-bold text-slate-800 mb-6">
                  Delivery Status
                </h2>
                <div className="relative">
                  {/* Vertical line */}
                  <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-slate-100" />
                  <ol className="space-y-6">
                    {STATUS_STEPS.map((step, idx) => {
                      const done = idx <= activeStep;
                      const active = idx === activeStep;
                      const isDeliveredStep = step.key === "delivered";
                      const useGreen =
                        isDeliveredStep && done
                          ? true
                          : done && !active;
                      const usePrimary = active && !isDeliveredStep;
                      return (
                        <li
                          key={step.key}
                          className="flex items-start gap-4 pl-12 relative"
                        >
                          <div
                            className={`absolute left-0 w-10 h-10 rounded-full flex items-center justify-center transition-all
                              ${
                                usePrimary
                                  ? "bg-primary shadow-lg shadow-primary/25 scale-110"
                                  : useGreen
                                    ? "bg-green-500 shadow-lg shadow-green-500/20"
                                    : "bg-slate-100"
                              }`}
                          >
                            <span
                              className={`material-symbols-outlined text-[18px] ${useGreen || usePrimary ? "text-white" : "text-slate-400"}`}
                              style={{ fontVariationSettings: '"FILL" 1' }}
                            >
                              {step.icon}
                            </span>
                          </div>
                          <div
                            className={`pt-2 ${done ? "text-slate-800" : "text-slate-400"}`}
                          >
                            <p
                              className={`text-sm font-semibold ${
                                isDeliveredStep && done
                                  ? "text-green-600"
                                  : usePrimary
                                    ? "text-primary"
                                    : ""
                              }`}
                            >
                              {step.label}
                            </p>
                            {active && (
                              <p
                                className={`text-xs mt-0.5 ${
                                  isDeliveredStep
                                    ? "text-green-600 font-medium"
                                    : "text-slate-500"
                                }`}
                              >
                                {isDeliveredStep
                                  ? "Delivery complete"
                                  : "Current status"}
                              </p>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ol>
                </div>
              </div>
            )}

            {isCancelled && (
              <div className="bg-red-50 border border-red-200 rounded-2xl p-6 mb-6 flex items-center gap-4">
                <span
                  className="material-symbols-outlined text-3xl text-red-400"
                  style={{ fontVariationSettings: '"FILL" 1' }}
                >
                  cancel
                </span>
                <div>
                  <p className="font-semibold text-slate-800">
                    This order was cancelled.
                  </p>
                  <Link
                    to="/deliverypage"
                    className="text-sm text-primary hover:underline font-medium"
                  >
                    Place a new order →
                  </Link>
                </div>
              </div>
            )}

            {/* Order details */}
            <div className="grid sm:grid-cols-2 gap-4">
              {/* Pickup */}
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
                <div className="flex items-center gap-2 mb-3">
                  <span
                    className="material-symbols-outlined text-primary text-[20px]"
                    style={{ fontVariationSettings: '"FILL" 1' }}
                  >
                    location_on
                  </span>
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-widest">
                    Pickup
                  </p>
                </div>
                <p className="text-sm text-slate-700 font-medium mb-1">
                  {order.pickupAddress}
                </p>
                <p className="text-xs text-slate-500">
                  {order.senderFirstName} {order.senderLastName} ·{" "}
                  {order.senderPhone}
                </p>
              </div>
              {/* Delivery */}
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
                <div className="flex items-center gap-2 mb-3">
                  <span
                    className="material-symbols-outlined text-green-500 text-[20px]"
                    style={{ fontVariationSettings: '"FILL" 1' }}
                  >
                    flag
                  </span>
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-widest">
                    Delivery
                  </p>
                </div>
                <p className="text-sm text-slate-700 font-medium mb-1">
                  {order.deliveryAddress}
                </p>
                <p className="text-xs text-slate-500">
                  {order.recipientFirstName} {order.recipientLastName} ·{" "}
                  {order.recipientPhone}
                </p>
              </div>
              {/* Package */}
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
                <div className="flex items-center gap-2 mb-3">
                  <span
                    className="material-symbols-outlined text-amber-500 text-[20px]"
                    style={{ fontVariationSettings: '"FILL" 1' }}
                  >
                    inventory_2
                  </span>
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-widest">
                    Package
                  </p>
                </div>
                <p className="text-sm text-slate-700 font-medium">
                  {order.packageDescription}
                </p>
                {order.packageWeight && (
                  <p className="text-xs text-slate-500 mt-1">
                    {order.packageWeight} kg
                  </p>
                )}
              </div>
              {/* Payment */}
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
                <div className="flex items-center gap-2 mb-3">
                  <span
                    className="material-symbols-outlined text-blue-500 text-[20px]"
                    style={{ fontVariationSettings: '"FILL" 1' }}
                  >
                    payments
                  </span>
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-widest">
                    Payment
                  </p>
                </div>
                <p className="text-sm font-bold text-slate-800">
                  ₦{order.amount.toLocaleString()}
                </p>
                <p className="text-xs text-slate-500 capitalize mt-0.5">
                  {order.paymentMethod}
                </p>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
